/**
 * Session Goal Manager
 * Generates daily rotating goals and tracks progress via EventBus
 */

import { SessionGoal, SessionGoalType, GameEvent, Quality, QUALITY_ORDER, TrapPullResult } from '@/types';
import { FISH_DEFINITIONS, SESSION_GOAL_TEMPLATES } from '@/data';
import { SessionGoalTemplate } from '@/data/goals/sessionGoalTemplates';
import { eventBus } from '@/services/EventBus';
import { useGameStore } from '@/state';
import toast from 'react-hot-toast';

const STORAGE_KEY = 'session_goals';
const GOALS_PER_DAY = 3;

/** Quality tiers that can appear in catch-quality goals */
const QUALITY_GOAL_TIERS: Quality[] = [Quality.RARE, Quality.EPIC];

/**
 * Simple seeded PRNG (same date = same goals)
 */
function seededRandom(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 1664525 + 1013904223) & 0xFFFFFFFF;
    return (s >>> 0) / 0xFFFFFFFF;
  };
}

/**
 * Derive a numeric seed from a date string like "2026-02-13"
 */
function dateSeed(dateStr: string): number {
  return dateStr.split('').reduce((sum, c) => sum + c.charCodeAt(0), 0);
}

export class SessionGoalManager {
  private goals: SessionGoal[] = [];
  private lastResetDate = '';
  private totalGoalsCompleted = 0;
  private unsubscribers: Array<() => void> = [];

  // ── Public API ──

  /** Start listening for events and load/reset goals */
  start(): void {
    this.load();
    this.checkAndReset();

    this.unsubscribers.push(
      eventBus.on(GameEvent.FISH_CAUGHT, (payload) => {
        this.onFishCaught(payload.fish.definitionId, payload.fish.quality);
      }),
      eventBus.on(GameEvent.FISH_SOLD, (payload) => {
        this.onFishSold(payload);
      }),
      eventBus.on(GameEvent.SWEET_SPOT_HIT, (payload) => {
        if (payload.tier === 'perfect' || payload.tier === 'good') {
          this.onSweetSpotHit();
        }
      }),
      eventBus.on(GameEvent.CURRENCY_GAINED, (payload) => {
        this.onGoldEarned(payload.amount);
      }),
      eventBus.on(GameEvent.TRAP_PULLED, (result) => {
        this.onTrapPulled(result);
      }),
    );
  }

  /** Stop listening for events */
  stop(): void {
    this.unsubscribers.forEach((u) => u());
    this.unsubscribers = [];
  }

  /** Get the current set of daily goals */
  getGoals(): SessionGoal[] {
    return this.goals;
  }

  /** Get lifetime total goals completed */
  getTotalCompleted(): number {
    return this.totalGoalsCompleted;
  }

  /** Check if it's a new day and reset goals if needed */
  checkAndReset(): void {
    const today = new Date().toISOString().slice(0, 10);
    if (this.lastResetDate !== today) {
      this.goals = this.generateDailyGoals(today);
      this.lastResetDate = today;
      this.save();
      eventBus.emit(GameEvent.SESSION_GOALS_RESET, {
        goals: this.goals.map((g) => ({
          id: g.id,
          description: g.description,
          targetValue: g.targetValue,
        })),
      });
    }
  }

  // ── Goal Generation ──

  generateDailyGoals(dateStr?: string): SessionGoal[] {
    const today = dateStr ?? new Date().toISOString().slice(0, 10);
    const rng = seededRandom(dateSeed(today));

    // Pick GOALS_PER_DAY non-duplicate template indices
    const usedIndices = new Set<number>();
    const pickedTemplates: SessionGoalTemplate[] = [];

    while (pickedTemplates.length < GOALS_PER_DAY && usedIndices.size < SESSION_GOAL_TEMPLATES.length) {
      const idx = Math.floor(rng() * SESSION_GOAL_TEMPLATES.length);
      if (!usedIndices.has(idx)) {
        usedIndices.add(idx);
        pickedTemplates.push(SESSION_GOAL_TEMPLATES[idx]);
      }
    }

    return pickedTemplates.map((template, i) =>
      this.instantiateGoal(template, rng, today, i),
    );
  }

  // ── Private Helpers ──

  private instantiateGoal(
    template: SessionGoalTemplate,
    rng: () => number,
    dateStr: string,
    index: number,
  ): SessionGoal {
    const [minTarget, maxTarget] = template.targetRange;
    const targetValue = minTarget + Math.floor(rng() * (maxTarget - minTarget + 1));

    let description = template.descriptionTemplate.replace('{count}', String(targetValue));
    let speciesId: string | undefined;
    let minQuality: Quality | undefined;

    // Resolve species placeholder
    if (template.useSpecies) {
      const speciesIdx = Math.floor(rng() * FISH_DEFINITIONS.length);
      const species = FISH_DEFINITIONS[speciesIdx];
      speciesId = species.id;
      description = description.replace('{species}', species.name);
    }

    // Resolve quality placeholder
    if (template.useQuality) {
      const qualityIdx = Math.floor(rng() * QUALITY_GOAL_TIERS.length);
      minQuality = QUALITY_GOAL_TIERS[qualityIdx];
      const qualityName = minQuality.charAt(0).toUpperCase() + minQuality.slice(1);
      description = description.replace('{quality}', qualityName);
    }

    const rewardGold = Math.round(template.baseRewardGold + targetValue * template.rewardPerTarget);
    const rewardXP = Math.round(template.baseRewardXP + targetValue * 2);

    return {
      id: `goal_${dateStr}_${index}`,
      description,
      type: template.type,
      targetValue,
      currentValue: 0,
      completed: false,
      rewardGold,
      rewardXP,
      speciesId,
      minQuality,
    };
  }

  // ── Event Handlers ──

  private onFishCaught(definitionId: string, quality: Quality): void {
    for (const goal of this.goals) {
      if (goal.completed) continue;

      if (goal.type === SessionGoalType.CATCH_FISH) {
        this.incrementGoal(goal, 1);
      } else if (goal.type === SessionGoalType.CATCH_SPECIES && goal.speciesId === definitionId) {
        this.incrementGoal(goal, 1);
      } else if (goal.type === SessionGoalType.CATCH_QUALITY && goal.minQuality) {
        if (QUALITY_ORDER[quality] >= QUALITY_ORDER[goal.minQuality]) {
          this.incrementGoal(goal, 1);
        }
      }
    }
  }

  private onFishSold(transaction: { fish: Array<{ count: number }>; finalValue: number }): void {
    const totalFishCount = transaction.fish.reduce((sum, stack) => sum + stack.count, 0);
    for (const goal of this.goals) {
      if (goal.completed) continue;

      if (goal.type === SessionGoalType.SELL_FISH) {
        this.incrementGoal(goal, totalFishCount);
      }
    }
  }

  private onSweetSpotHit(): void {
    for (const goal of this.goals) {
      if (goal.completed) continue;

      if (goal.type === SessionGoalType.SWEET_SPOT_HITS) {
        this.incrementGoal(goal, 1);
      }
    }
  }

  private onGoldEarned(amount: number): void {
    for (const goal of this.goals) {
      if (goal.completed) continue;

      if (goal.type === SessionGoalType.EARN_GOLD) {
        this.incrementGoal(goal, amount);
      }
    }
  }

  private onTrapPulled(result: TrapPullResult): void {
    for (const goal of this.goals) {
      if (goal.completed) continue;

      if (goal.type === SessionGoalType.PULL_TRAPS) {
        this.incrementGoal(goal, 1);
      } else if (goal.type === SessionGoalType.CATCH_CRUSTACEANS) {
        // Count creatures that are crustaceans or shellfish
        const crustaceanCount = result.creatures.filter(
          (c) => c.definition.category === 'crustacean' || c.definition.category === 'shellfish',
        ).length;
        if (crustaceanCount > 0) {
          this.incrementGoal(goal, crustaceanCount);
        }
      }
    }
  }

  private incrementGoal(goal: SessionGoal, amount: number): void {
    goal.currentValue = Math.min(goal.currentValue + amount, goal.targetValue);
    this.checkCompletion(goal);
    this.save();
  }

  private checkCompletion(goal: SessionGoal): void {
    if (goal.currentValue >= goal.targetValue && !goal.completed) {
      goal.completed = true;
      this.totalGoalsCompleted++;

      // Apply gold reward
      useGameStore.getState().addCurrency(goal.rewardGold);

      eventBus.emit(GameEvent.SESSION_GOAL_COMPLETED, {
        goalId: goal.id,
        rewardGold: goal.rewardGold,
        rewardXP: goal.rewardXP,
      });

      toast.success(`Goal complete: ${goal.description}! +${goal.rewardGold}g`, {
        icon: '🎯',
        duration: 3000,
      });
    }
  }

  // ── Persistence ──

  private save(): void {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          goals: this.goals,
          lastResetDate: this.lastResetDate,
          totalGoalsCompleted: this.totalGoalsCompleted,
        }),
      );
    } catch (error) {
      console.error('Failed to save session goals:', error);
    }
  }

  private load(): void {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const data = JSON.parse(saved);
        this.goals = data.goals || [];
        this.lastResetDate = data.lastResetDate || '';
        this.totalGoalsCompleted = data.totalGoalsCompleted || 0;
      }
    } catch (error) {
      console.error('Failed to load session goals:', error);
      this.goals = [];
      this.lastResetDate = '';
      this.totalGoalsCompleted = 0;
    }
  }
}

export const sessionGoalManager = new SessionGoalManager();
